/**
 * DangoteCarbonStrategy — Decision Rigor Engine
 * Route: /corporate/carbon-strategy
 * Role: "What should we do?" — Priority and ranking ONLY. No recomputation.
 * Input: Promoted opportunities from Carbon Value Bridge.
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { RefreshCw, Info, ArrowRight, TrendingDown, ChevronDown, ChevronUp } from "lucide-react";
import client from "@/api/client";

// ── Status / Flag configs ────────────────────────────────────────────────────
const STATUS_COLORS = {
  identified:             "bg-slate-100 text-slate-500 border-slate-200",
  promoted_to_strategy:   "bg-blue-100 text-blue-700 border-blue-200",
  selected_for_execution: "bg-teal-100 text-teal-700 border-teal-200",
  structuring_in_progress:"bg-amber-100 text-amber-700 border-amber-200",
  structuring_ready:      "bg-emerald-100 text-emerald-700 border-emerald-200",
  financing_review:       "bg-purple-100 text-purple-700 border-purple-200",
  financing_ready:        "bg-emerald-100 text-emerald-800 border-emerald-300",
  executed:               "bg-[#0F172A] text-white border-transparent",
};
const STATUS_LABELS = {
  identified:"Identified", promoted_to_strategy:"In Strategy",
  selected_for_execution:"Selected", structuring_in_progress:"Structuring",
  structuring_ready:"Ready", financing_review:"In Financing",
  financing_ready:"Finance Ready", executed:"Executed",
};
const FLAG_CONFIG = {
  quick_win:    { label: "Quick Win",     color: "bg-teal-100 text-teal-700 border-teal-200" },
  high_impact:  { label: "High Impact",   color: "bg-red-100 text-red-700 border-red-200" },
  strategic_bet:{ label: "Strategic Bet", color: "bg-purple-100 text-purple-700 border-purple-200" },
};
const PATHWAY_COLORS = {
  vcm:"bg-teal-100 text-teal-700 border-teal-200",
  art6:"bg-blue-100 text-blue-700 border-blue-200",
  internal:"bg-slate-100 text-slate-500 border-slate-200",
};
const SORT_OPTIONS = [
  { value: "composite_score",       label: "Composite Score" },
  { value: "reduction_tco2e_yr",    label: "GHG Impact" },
  { value: "irr_pct",               label: "ROI / IRR" },
  { value: "payback_years",         label: "Payback" },
  { value: "guaranteed_value_usd_yr","label": "Guaranteed Value" },
];

// ── Score Bar ────────────────────────────────────────────────────────────────
function ScoreBar({ label, score, color = "bg-teal-500" }) {
  return (
    <div className="mb-1">
      <div className="flex justify-between text-[9px] mb-0.5">
        <span className="text-slate-500">{label}</span>
        <span className="text-slate-700 font-bold">{score}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

// Compute "Why Selected" reason tag
function whySelected(opp) {
  const irr = opp.irr_pct || 0;
  const pb = opp.payback_years || 99;
  const capex = opp.capex_usd || 0;
  const red = opp.reduction_tco2e_yr || 0;
  if (irr >= 20) return { label: "High IRR", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  if (pb <= 3 && capex <= 15_000_000) return { label: "Fast Payback", cls: "bg-teal-100 text-teal-700 border-teal-200" };
  if (capex <= 8_000_000) return { label: "Low CAPEX", cls: "bg-blue-100 text-blue-700 border-blue-200" };
  if (red >= 200_000) return { label: "Strategic Decarbonization", cls: "bg-purple-100 text-purple-700 border-purple-200" };
  return { label: "Portfolio Balance", cls: "bg-slate-100 text-slate-500 border-slate-200" };
}

// ── Opportunity Card ─────────────────────────────────────────────────────────
function OppCard({ opp, rank, onTransition, transitioning }) {
  const [expanded, setExpanded] = useState(false);
  const canSendToStructuring = opp.status === "promoted_to_strategy";

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white"
      data-testid={`strategy-card-${opp.opp_id}`}>
      {/* Header row */}
      <button className="w-full flex items-start gap-3 px-5 py-4 hover:bg-slate-50 text-left"
        onClick={() => setExpanded(v => !v)}>
        {/* Rank badge */}
        <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
          #{rank}
        </div>
        <div className="flex-1 min-w-0 mr-3">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {/* Decision flags */}
            {(opp.flags || []).map(f => (
              <span key={f} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${FLAG_CONFIG[f]?.color}`}>
                {FLAG_CONFIG[f]?.label || f}
              </span>
            ))}
            {/* Why Selected tag */}
            {(() => { const ws = whySelected(opp); return (
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ws.cls}`}>
                Why: {ws.label}
              </span>
            ); })()}
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${PATHWAY_COLORS[opp.pathway]||PATHWAY_COLORS.internal}`}>
              {(opp.pathway||"internal").toUpperCase()}
            </span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[opp.status]||STATUS_COLORS.identified}`}>
              {STATUS_LABELS[opp.status]||opp.status}
            </span>
          </div>
          <p className="text-sm font-bold text-slate-900">{opp.name}</p>
          <p className="text-xs text-slate-400">{opp.bu_name} · {opp.asset_id} · {opp.driver_label}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Composite score */}
          <div className="text-right">
            <p className="text-lg font-bold text-teal-700">{opp.composite_score}</p>
            <p className="text-[9px] text-slate-400">/ 100</p>
          </div>
          {expanded ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Metrics */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Key Metrics</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["Reduction",    `${Number(opp.reduction_tco2e_yr||0).toLocaleString()} tCO₂e/yr`, "text-emerald-700"],
                  ["Guaranteed",   `$${Number((opp.guaranteed_value_usd_yr||0)/1e6).toFixed(1)}M/yr`, "text-teal-700"],
                  ["Carbon (Base)",`$${Number((opp.carbon_rev_base_usd_yr||0)/1e6).toFixed(1)}M/yr`, "text-blue-700"],
                  ["CAPEX",        `$${Number((opp.capex_usd||0)/1e6).toFixed(1)}M`,                "text-slate-900"],
                  ["IRR",          `${opp.irr_pct||"—"}%`,                                          "text-emerald-700"],
                  ["Payback",      `${opp.payback_years||"—"}yr`,                                   "text-slate-900"],
                ].map(([l,v,c]) => (
                  <div key={l} className="bg-slate-50 rounded-lg px-2.5 py-2">
                    <p className="text-slate-400 mb-0.5">{l}</p>
                    <p className={`font-bold ${c}`}>{v}</p>
                  </div>
                ))}
              </div>
              {/* SDG tags */}
              {(opp.sdg_tags||[]).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {opp.sdg_tags.map(s => (
                    <span key={s} className="text-[9px] font-bold bg-slate-800 text-white px-1.5 py-0.5 rounded">SDG {s}</span>
                  ))}
                </div>
              )}
            </div>
            {/* Scoring breakdown */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Score Breakdown</p>
              {Object.entries(opp.score_breakdown || {}).map(([k, v]) => (
                <ScoreBar key={k} label={k.charAt(0).toUpperCase()+k.slice(1)} score={v}
                  color={v >= 70 ? "bg-emerald-500" : v >= 40 ? "bg-amber-400" : "bg-red-400"} />
              ))}
            </div>
          </div>

          {/* Calculation traceability */}
          {opp.calculation_id && (
            <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-xs flex items-center gap-3">
              <span className="text-slate-400">Calc ID:</span>
              <code className="text-teal-700 font-mono">{opp.calculation_id}</code>
              <span className="text-slate-400">Hash:</span>
              <code className="text-slate-600 font-mono">{opp.calc_hash}...</code>
              <span className="text-slate-400">Baseline:</span>
              <span className="text-slate-600">{opp.baseline_type}</span>
            </div>
          )}

          {/* Action */}
          {canSendToStructuring && (
            <button onClick={() => onTransition(opp.opp_id, "structuring_in_progress")}
              disabled={transitioning}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50"
              data-testid={`send-to-structuring-${opp.opp_id}`}>
              {transitioning ? <RefreshCw size={13} className="animate-spin"/> : <ArrowRight size={13}/>}
              Send to Structuring
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DangoteCarbonStrategy() {
  const navigate       = useNavigate();
  const [sp]           = useSearchParams();
  const buFilter       = sp.get("bu");
  const { period, selectedPeriod, vbCache, vbLoading, invalidateVb } = useDangote();
  const [sortBy, setSortBy]       = useState("composite_score");
  const [filterType, setFilterType] = useState("all");
  const [showAll, setShowAll]     = useState(false);
  const [transitioning, setTrans] = useState(false);

  const vbData  = selectedPeriod ? (vbCache[selectedPeriod] || null) : null;
  const loading = vbLoading && !vbData;

  const handleTransition = async (opp_id, newStatus) => {
    setTrans(true);
    await client.post(`/dangote/opportunities/${opp_id}/transition`, { status: newStatus });
    invalidateVb(selectedPeriod);
    setTrans(false);
  };

  const allOpps   = vbData?.opportunities || [];
  const portfolio = vbData?.portfolio     || {};
  const byStatus  = vbData?.by_status     || {};

  // Determine which opps to show
  const promoted = allOpps.filter(o => ["promoted_to_strategy","selected_for_execution",
    "structuring_in_progress","structuring_ready","financing_review","financing_ready","executed"].includes(o.status));
  const hasPromoted = promoted.length > 0;
  const baseList    = (showAll || !hasPromoted) ? allOpps : promoted;

  // Filter
  const filtered = filterType === "all" ? baseList
    : filterType === "quick_win"     ? baseList.filter(o => (o.flags||[]).includes("quick_win"))
    : filterType === "high_impact"   ? baseList.filter(o => (o.flags||[]).includes("high_impact"))
    : filterType === "strategic_bet" ? baseList.filter(o => (o.flags||[]).includes("strategic_bet"))
    : filterType === "vcm"           ? baseList.filter(o => o.vcm_eligible)
    : filterType === "art6"          ? baseList.filter(o => o.art6_eligible)
    : baseList;

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "payback_years") return (a.payback_years||99) - (b.payback_years||99);
    return (b[sortBy]||0) - (a[sortBy]||0);
  });

  if (loading && !vbData) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Carbon Strategy" />
          <p className="text-xs text-slate-400 mt-0.5">
            Decision engine · Prioritize and rank opportunities · {period?.label}
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricCard label="In Strategy"       value={byStatus.promoted_to_strategy||0}   color="text-blue-700" />
        <MetricCard label="In Structuring"    value={(byStatus.structuring_in_progress||0)+(byStatus.structuring_ready||0)} color="text-amber-700" />
        <MetricCard label="In Financing"      value={(byStatus.financing_review||0)+(byStatus.financing_ready||0)} color="text-purple-700" />
        <MetricCard label="Executed"          value={byStatus.executed||0}               color="text-emerald-700" />
        <MetricCard label="Reduction Pipeline" value={`${Number((portfolio.total_reduction_tco2e_yr||0)/1000).toFixed(0)}K`} unit="tCO₂e/yr" color="text-teal-700" />
      </div>

      {/* Source note + show all toggle */}
      <div className={`rounded-xl px-4 py-3 flex items-start gap-2 ${hasPromoted?"bg-blue-50 border border-blue-200":"bg-amber-50 border border-amber-200"}`}>
        <Info size={12} className={`flex-shrink-0 mt-0.5 ${hasPromoted?"text-blue-500":"text-amber-500"}`}/>
        <div className="flex-1 text-xs">
          <span className={hasPromoted?"text-blue-700":"text-amber-700"}>
            {hasPromoted
              ? `Showing ${promoted.length} opportunities from Carbon Value Bridge pipeline`
              : "No promoted items yet — promote from Carbon Value Bridge first"}
          </span>
          {!hasPromoted && (
            <button onClick={()=>navigate("/corporate/value-bridge")} className="ml-2 underline text-amber-700">Open Value Bridge →</button>
          )}
          {hasPromoted && (
            <button onClick={()=>setShowAll(v=>!v)} className="ml-2 underline text-blue-600">
              {showAll ? "Show pipeline only" : `Show all ${allOpps.length} →`}
            </button>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500 font-semibold">Sort:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700">
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex gap-1.5">
          {[
            ["all","All"],["quick_win","Quick Wins"],["high_impact","High Impact"],
            ["strategic_bet","Strategic Bets"],["vcm","VCM"],
          ].map(([k,l]) => (
            <button key={k} onClick={() => setFilterType(k)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                filterType===k ? "bg-[#0F172A] text-white border-transparent" : "bg-white border-slate-200 text-slate-500"
              }`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Ranked opportunity cards */}
      {sorted.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <p className="text-slate-500 text-sm">No opportunities match this filter</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((o, i) => (
            <OppCard key={o.opp_id} opp={o} rank={i+1}
              onTransition={handleTransition} transitioning={transitioning} />
          ))}
        </div>
      )}

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <p className="text-xs text-amber-700 italic">
          Scoring and ranking are indicative. Composite score weights: GHG Impact 30% · Financial Return 25% ·
          Payback 20% · CAPEX efficiency 10% · SDG alignment 10% · Readiness 5%.
          Not investment advice. Independent financial due diligence required.
        </p>
      </div>
    </div>
  );
}
